<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useElementSize } from '@vueuse/core';
import { ApexOptions } from 'apexcharts';
import { getHighlightedColors } from '@/lib/chart-helpers';
import { DashboardWidget, Serie } from '@/interfaces/Dashboard';
import ConnxGenericApexChart from './ConnxGenericApexChart.vue';

const props = defineProps<{ width: number; height: number; widget: DashboardWidget }>();

const componentRef = ref();
const cardsRef = ref();
const cardOneData = ref();
const cardTwoData = ref();
const { height: cardsHeight } = useElementSize(cardsRef);
const chartHeight = computed(() => {
  return props.height - cardsHeight.value;
});

const chartOptions = ref<ApexOptions>({});

const formatData = () => {
  const comp = componentRef.value as any;
  if (comp && typeof comp.formatData === 'function') {
    comp.formatData();
  }
};

const forceOptionsUpdate = (opts: ApexOptions) => {
  const comp = componentRef.value as any;
  if (comp && typeof comp.forceOptionsUpdate === 'function') {
    return comp.forceOptionsUpdate(opts);
  }

  return null;
};

const dataUpdated = (data: any) => {
  const series: Serie[] = Array.isArray(data.data) ? data.data : data.data?.series ?? [];

  const maxIndex = series.reduce((maxIdx, item, idx, array) => (item.data > array[maxIdx].data ? idx : maxIdx), 0);

  const totalValues = series.reduce<number>((carry, item) => {
    if (Array.isArray(item.data)) {
      return carry + item.data.reduce<number>((sum, v) => sum + Number(v), 0);
    }

    return carry + Number(item.data);
  }, 0);

  const average = data.data.average ?? Number((totalValues / series.length).toFixed(1));

  const highlighted = getHighlightedColors([maxIndex], ['var(--connx-danger)']);

  chartOptions.value = {
    colors: highlighted,
  };

  const showPercent = props.widget.defaultJsonProperties.currentDisplayUnit === 'percent';
  cardOneData.value = `${series[maxIndex].name} (${series[maxIndex].data}${showPercent ? '%' : null})`;

  cardTwoData.value = `${average}${showPercent ? '%' : null}`;

  forceOptionsUpdate(chartOptions.value);
};

watch(
  () => props.widget.defaultJsonProperties.currentDisplayType,
  async (val: string) => {
    formatData();
  }
);
</script>
<template>
  <div>
    <connx-generic-apex-chart
      :height="chartHeight"
      :options="chartOptions"
      ref="componentRef"
      :width="width"
      :widget="widget"
      @data-updated="dataUpdated"
    >
    </connx-generic-apex-chart>
    <div ref="cardsRef">
      <div class="tw-flex tw-justify-center tw-pt-10" v-if="widget.defaultJsonProperties.spotlightTitles">
        <div class="tw-grid tw-grid-cols-2 tw-gap-4 tw-place-items-stretch">
          <div
            class="tw-border-connx-danger tw-bg-connx-danger-tint-95 tw-px-4 tw-py-2 tw-border-0 tw-border-solid tw-border-l-8 tw-rounded-r-md"
          >
            <h4 class="tw-text-base tw-text-connx-danger">{{
              widget.defaultJsonProperties.spotlightTitles?.cardOne
            }}</h4>
            <p class="tw-text-xl tw-mb-0 tw-text-connx-black">{{ cardOneData }}</p>
          </div>
          <div
            class="tw-bg-connx-grey-tint-95 tw-px-4 tw-py-2 tw-border-0 tw-border-solid tw-border-l-8 tw-rounded-r-md"
          >
            <h4 class="tw-text-base">{{ widget.defaultJsonProperties.spotlightTitles?.cardTwo }}</h4>
            <p class="tw-text-xl tw-mb-0 tw-text-connx-black">{{ cardTwoData }}</p>
          </div>
          <div> </div>
        </div>
      </div>
    </div>
  </div>
</template>
